import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  DEFAULT_TIME_SERIES_ALLOW_EDIT_SUBMITTED_ROWS,
  DEFAULT_TIME_SERIES_BLOCK_FUTURE_DATES,
  DEFAULT_TIME_SERIES_DUPLICATE_HANDLING,
  DEFAULT_TIME_SERIES_PERIOD,
  DEFAULT_TIME_SERIES_TIMESTAMP_FIELD,
  DEFAULT_TIME_SERIES_TIMEZONE,
} from '@anessa/shared-models';
import {
  ImportOptions,
  ImportResult,
  MatchedSheet,
  SheetImporter,
  SheetParseResult,
} from '../schemas/sheet-importer.interface';
import { CsvSeparator } from '../../../shared/services/file-export/file-export.service';
import { TimeSeriesImportService } from '../../../features/timeseries-data/services/timeseries-import.service';
import { TimeSeriesTable } from '../../../features/timeseries-data/models/timeseries-table.model';
import { TimeSeriesDataPointsService } from '../../../features/timeseries-data/services/timeseries-data-points.service';
import { TimeSeriesTablesService } from '../../../features/timeseries-data/services/timeseries-tables.service';

const BATCH_SIZE = 250;

function extractHttpErrorMessage(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    const body = err.error as { message?: string | string[] } | null;
    if (body && typeof body === 'object') {
      if (Array.isArray(body.message)) return body.message.join('; ');
      if (typeof body.message === 'string') return body.message;
    }
  }
  return err instanceof Error ? err.message : 'Import failed';
}

@Injectable({ providedIn: 'root' })
export class TimeseriesSheetImporterService implements SheetImporter {
  private readonly timeseriesTableService = inject(TimeSeriesTablesService);
  private readonly timeseriesImportService = inject(TimeSeriesImportService);
  private readonly timeseriesDataPointsService = inject(
    TimeSeriesDataPointsService,
  );

  matchSheet(sheetName: string, sheetIndex: number): MatchedSheet | null {
    const matchedTable = this.timeseriesTableService
      .tables()
      .find(
        (table) =>
          table.name.toLowerCase().trim() === sheetName.toLowerCase().trim(),
      );

    if (!matchedTable) return null;

    return {
      sheetName,
      sheetIndex,
      sectionLabel: matchedTable.name,
      meta: matchedTable,
    };
  }

  /** Manually map a skipped sheet to a user-picked existing table, bypassing
   *  the name-based lookup in matchSheet(). */
  matchSheetToTable(
    sheetName: string,
    sheetIndex: number,
    table: TimeSeriesTable,
  ): MatchedSheet {
    return {
      sheetName,
      sheetIndex,
      sectionLabel: table.name,
      meta: table,
    };
  }

  /** Create a brand-new table for a skipped sheet, inferring every column
   *  from the sheet's own header row (reusing the same detection/inference
   *  code as an unknown header on an existing table). The new table's
   *  timestamp column must match DEFAULT_TIME_SERIES_TIMESTAMP_FIELD — a
   *  sheet without that header will come back with every row reported as a
   *  missing-timestamp error rather than any row being silently dropped. */
  async createTableForSheet(
    sheetName: string,
    file: File,
    sheetIndex: number,
    separator: CsvSeparator,
    plantId: string,
    projectId: string,
  ): Promise<MatchedSheet> {
    const shellTable = TimeSeriesTable.fromDto({
      id: '',
      name: sheetName,
      description: '',
      schemaVersion: 1,
      plantId,
      projectId,
      fields: [],
      displayTimezone: DEFAULT_TIME_SERIES_TIMEZONE,
      ingestion: {
        timezone: DEFAULT_TIME_SERIES_TIMEZONE,
        timestampField: DEFAULT_TIME_SERIES_TIMESTAMP_FIELD,
        duplicateHandling: DEFAULT_TIME_SERIES_DUPLICATE_HANDLING,
        period: DEFAULT_TIME_SERIES_PERIOD,
        allowEditSubmittedRows: DEFAULT_TIME_SERIES_ALLOW_EDIT_SUBMITTED_ROWS,
        blockFutureDates: DEFAULT_TIME_SERIES_BLOCK_FUTURE_DATES,
      },
      userId: '',
      order: 0,
      visible: true,
    });

    const initial = await this.timeseriesImportService.parseFile(
      file,
      shellTable,
      separator,
      { sheetIndex },
    );
    const previewTable = this.timeseriesImportService.buildTableWithUnknownColumns(
      shellTable,
      initial.unknownHeaders,
      initial.inferredTypes,
    );

    const created = await firstValueFrom(
      this.timeseriesTableService.create({
        name: sheetName,
        plantId,
        projectId,
        fields: previewTable.columns.map((column, index) => ({
          key: column.key,
          label: column.label,
          importColumnName: column.importColumnName,
          dataType: column.dataType,
          required: column.required,
          unit: column.unit,
          order: column.order ?? index,
          visible: column.visible,
        })),
      }),
    );
    this.timeseriesTableService.addTable(created);

    return {
      sheetName,
      sheetIndex,
      sectionLabel: created.name,
      meta: TimeSeriesTable.fromDto(created),
    };
  }

  async parseSheet(
    file: File,
    match: MatchedSheet,
    separator: CsvSeparator,
  ): Promise<SheetParseResult> {
    const table = match.meta as TimeSeriesTable;
    const initial = await this.timeseriesImportService.parseFile(
      file,
      table,
      separator,
      { sheetIndex: match.sheetIndex },
    );

    // Re-parse against a preview table with the unknown headers added as
    // columns so validRowCount/errors reflect what will actually be imported
    // — this is read-only (no persistence), so it stays safe to run in
    // parallel across sheets in MultiTabImportService.parseAll().
    const previewTable = this.timeseriesImportService.buildTableWithUnknownColumns(
      table,
      initial.unknownHeaders,
      initial.inferredTypes,
    );
    const result =
      previewTable.columns.length === table.columns.length
        ? initial
        : await this.timeseriesImportService.parseFile(
            file,
            previewTable,
            separator,
            { sheetIndex: match.sheetIndex },
          );

    return {
      validRowCount: result.points.length,
      errors: result.errors.map((e) => ({ row: e.row, message: e.messageKey })),
      parsed: result,
      newColumnCount: initial.unknownHeaders.length,
    };
  }

  async importRecords(
    match: MatchedSheet,
    parseResult: SheetParseResult,
    options: ImportOptions,
    file: File,
    separator: CsvSeparator,
  ): Promise<ImportResult> {
    const table = match.meta as TimeSeriesTable;
    const totals: ImportResult = { created: 0, updated: 0, errors: [] };

    // Re-fetch the table's live state and re-parse rather than trusting the
    // parseSheet-time snapshot: MultiTabImportService.importAll() runs
    // sequentially, so an earlier sheet in this same import may have already
    // added columns to this same table (e.g. via manual sheet-to-table
    // mapping). Recomputing here avoids clobbering that sheet's new columns.
    const currentTable =
      this.timeseriesTableService.getTableById(table.id) ?? table;
    const initial = await this.timeseriesImportService.parseFile(
      file,
      currentTable,
      separator,
      { sheetIndex: match.sheetIndex },
    );
    const previewTable = this.timeseriesImportService.buildTableWithUnknownColumns(
      currentTable,
      initial.unknownHeaders,
      initial.inferredTypes,
    );
    const result =
      previewTable.columns.length === currentTable.columns.length
        ? initial
        : await this.timeseriesImportService.parseFile(
            file,
            previewTable,
            separator,
            { sheetIndex: match.sheetIndex },
          );

    if (previewTable.columns.length > currentTable.columns.length) {
      try {
        const updated = await firstValueFrom(
          this.timeseriesTableService.update(currentTable.id, currentTable.plantId, {
            fields: previewTable.columns.map((column, index) => ({
              key: column.key,
              label: column.label,
              importColumnName: column.importColumnName,
              dataType: column.dataType,
              required: column.required,
              unit: column.unit,
              order: column.order ?? index,
              visible: column.visible,
            })),
          }),
        );
        this.timeseriesTableService.updateTable(updated);
      } catch (err) {
        return {
          created: 0,
          updated: 0,
          errors: [],
          sheetError: extractHttpErrorMessage(err),
        };
      }
    }

    const { points } = result;
    for (let i = 0; i < points.length; i += BATCH_SIZE) {
      const batch = points.slice(i, i + BATCH_SIZE);
      try {
        const inserted = await firstValueFrom(
          this.timeseriesDataPointsService.bulkCreate(table.id, {
            points: batch,
            duplicateHandlingOverride: options.duplicateHandling,
          }),
        );
        totals.created += inserted.length;
      } catch (err) {
        totals.errors.push({
          row: i + 1,
          message: extractHttpErrorMessage(err),
        });
      }
    }

    return totals;
  }
}
